import { PLANS } from '@auw/workspace-utils';
import i18n from '@auw/i18n';

const AVATAR_PALETTE = [
  '#f87171', '#fb923c', '#fbbf24', '#a3e635',
  '#34d399', '#22d3ee', '#60a5fa', '#818cf8',
  '#a78bfa', '#c084fc', '#f472b6', '#fb7185',
];

export function avatarColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

export function avatarInitial(name?: string | null, email?: string | null): string {
  const src = (name || email || 'U').trim();
  const first = src.charAt(0);
  return /[\u3131-\uD79D]/.test(first) ? first : first.toUpperCase();
}

export function formatDateOrDash(value?: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDateOnlyOrDash(value?: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function billingCycleLabel(cycle?: string | null): string {
  const normalized = String(cycle || '').trim().toLowerCase();
  if (normalized === 'monthly') return i18n.t('settings:planLabels.monthly');
  if (normalized === 'yearly') return i18n.t('settings:planLabels.yearly');
  return '';
}

export function normalizePlanKey(plan?: string | null): keyof typeof PLANS | null {
  const normalized = String(plan || '').trim().toLowerCase();
  if (!normalized) return null;
  if (!(normalized in PLANS)) return null;
  return normalized as keyof typeof PLANS;
}

export function billingStatusLabel(status?: string | null): string {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'trialing') return i18n.t('settings:planLabels.trial');
  if (normalized === 'active') return i18n.t('settings:planLabels.active');
  if (normalized === 'canceling') return i18n.t('settings:planLabels.cancelScheduled');
  if (normalized === 'past_due') return i18n.t('settings:planLabels.overdue');
  if (normalized === 'paused') return i18n.t('settings:planLabels.paused');
  if (normalized === 'canceled') return i18n.t('settings:planLabels.cancelled');
  return i18n.t('settings:planLabels.notConnected');
}

export function billingStatusBadgeVariant(status?: string | null): 'default' | 'info' | 'success' | 'warning' | 'error' {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'active' || normalized === 'trialing') return 'success';
  if (normalized === 'canceling') return 'warning';
  if (normalized === 'past_due' || normalized === 'paused') return 'error';
  if (normalized === 'canceled') return 'default';
  return 'info';
}

export function parseApiError(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) return fallback;
  const detailMatch = error.message.match(/"detail"\s*:\s*"([^"]+)"/);
  if (detailMatch?.[1]) return detailMatch[1];
  return error.message.replace(/^[A-Za-z ]+failed \(\d+\):\s*/i, '').trim() || fallback;
}

export function preOpenTabForNavigation(): Window | null {
  // 사용자 클릭 직후 탭을 먼저 열어야 비동기 API 이후에도 팝업 차단을 피할 수 있습니다.
  return window.open('about:blank', '_blank');
}

export function openExternalInNewTab(
  url: string,
  preOpenedTab?: Window | null,
  fallbackToCurrentTab = false,
): boolean {
  if (preOpenedTab && !preOpenedTab.closed) {
    preOpenedTab.location.replace(url);
    return true;
  }
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (opened) return true;
  if (fallbackToCurrentTab) {
    window.location.href = url;
    return true;
  }
  return false;
}

export function closeTabIfOpen(tab?: Window | null): void {
  if (tab && !tab.closed) tab.close();
}
